import { MaintenanceWindowsApiClients } from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { GzMaintenanceWindowSummaryWebComponent } from '../maintenanceWindowSummary';
import { getDefaultUiSettingsForMaintenanceWindow } from '../../../../utils/uiSettings/patchManagement/patch-management.values';
import { MaintenanceWindowPreviewPatchManagementWebComponentSchema } from '../../../../../../../maintenanceWindows/maintenanceWindowPreviewPatchManagement/types';
import { GzDialogWebComponent } from '../../../../../../../ui/gzDialog';
import { GzButtonWebComponent } from '../../../../../../../ui/gzButton';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzMaintenanceWindowsContainerWebComponent } from '../maintenanceWindowsContainer';
import { PatchManagementMaintenanceWindow } from '@nirvana/shared/dtos/types/policies/common';
import { GzToastNotificationWebComponent } from '../../../../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../../../../ui/gzToastNotification/type';
import { MaintenanceWindowTypes } from '../../../../../../../types/common/maintenanceWindowTypes';
import { MaintenanceWindowPreviewConverterV2 } from '../utils/MaintenanceWindowSummaryConverterV2';
import { selectPoliciesState } from '../../../../redux/slice';
import { store } from '@nirvana/redux/store';
import { getPhantom } from '../../../../redux/actions';

export class CreateMaintenanceWindowDialog {
  private readonly clients: MaintenanceWindowsApiClients;
  private readonly maintenanceWindowsContainerFormField: GzFormFieldWebComponent<
    GzMaintenanceWindowsContainerWebComponent,
    PatchManagementMaintenanceWindow[]
  >;
  private dialog: GzDialogWebComponent;

  public constructor(
    clients: MaintenanceWindowsApiClients,
    maintenanceWindowsContainerFormField: GzFormFieldWebComponent<
      GzMaintenanceWindowsContainerWebComponent,
      PatchManagementMaintenanceWindow[]
    >
  ) {
    this.clients = clients;
    this.maintenanceWindowsContainerFormField = maintenanceWindowsContainerFormField;
  }

  public show() {
    this.dialog = GzDialogWebComponent.computeCustomDialog(
      this.getWindowBody(),
      this.getWindowFooter(),
      this.getWindowTitle(),
      null,
      this.confirmCallback.bind(this),
      null,
      false
    );
  }

  private async confirmCallback() {
    const confirmBtn = this.dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm') as GzButtonWebComponent;
    this.setButtonLoading(confirmBtn, true);

    try {
      const state = selectPoliciesState(store.getState());
      const phantom = getPhantom(state);
      const userCompany = Application.Main.getUserCompany();

      // get policy company
      const company = phantom ? userCompany : PolicyPanelManager.getInstance().getPolicy('company', userCompany);
      // get suggested name for the new maintenance window in company
      const suggestedName = await this.clients.GetSuggestedNewMaintenanceWindowName(
        MaintenanceWindowTypes.PATCH_MANAGEMENT,
        company.id
      );

      // compute maintenance window frontend settings by adding the suggested name and company
      const maintenanceWindowFrontendSettings = {
        ...{ name: suggestedName },
        ...{ company: company },
        ...getDefaultUiSettingsForMaintenanceWindow(),
      };

      // create the maintenance window
      const response = await this.clients.CreateMaintenanceWindowItem(maintenanceWindowFrontendSettings);

      // set the new available maintenance windows and the new selected maintenance windows
      const availableMaintenanceWindows = [{ _id: response.data[0], name: suggestedName }];
      const selectedMaintenanceWindows = [{ id: response.data[0], name: suggestedName }];
      PolicyPanelManager.getInstance().setPatchManagementAvailableMaintenanceWindowsPolicyInfo(
        availableMaintenanceWindows
      );
      this.maintenanceWindowsContainerFormField.Input.availableMaintenanceWindows = availableMaintenanceWindows;
      this.maintenanceWindowsContainerFormField.Input.selectedMaintenanceWindows = selectedMaintenanceWindows;

      // show success toast notification
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn('Maintenance window successfully created.'),
        severity: ToastSeverity.SUCCESS,
      });
    } catch (error) {
      // show error toast notification
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn(
          'An unexpected error has occurred. Please try again. If the issue persists, contact Bitdefender Technical Support.'
        ),
        severity: ToastSeverity.ERROR,
      });
    } finally {
      this.dialog.close();
    }
  }

  private setButtonLoading(button: GzButtonWebComponent | null, isLoading: boolean) {
    if (button) {
      button.showSpinner = isLoading;
      button.disabled = isLoading;
    }
  }

  private getBodyTextWebComponent(): GzTextWebComponent {
    const text = new GzTextWebComponent();
    text.innerHTML = translateFn(
      'This maintenance window contains basic settings. You can edit the window later in Policies > Configuration Profiles.'
    );
    text.fontSize = FontSizes.PARAGRAPH_13;
    text.fontWeight = FontWeights.NORMAL;
    text.style.marginBottom = 'calc(var(--spacer) * 2)';

    return text;
  }

  private getBodySummaryComponent(): GzMaintenanceWindowSummaryWebComponent {
    const summary = new GzMaintenanceWindowSummaryWebComponent();
    summary.isTitleHidden = true;

    const previewConverter = new MaintenanceWindowPreviewConverterV2();
    previewConverter.injectDependencies({
      translateFn: translateFn,
    });
    summary.summaryData = previewConverter.convertToTemplateDataV2(
      getDefaultUiSettingsForMaintenanceWindow() as unknown as MaintenanceWindowPreviewPatchManagementWebComponentSchema
    );

    return summary;
  }

  private getWindowBody(): HTMLDivElement {
    const windowBodyHtml = document.createElement('div');
    windowBodyHtml.appendChild(this.getBodyTextWebComponent());
    windowBodyHtml.appendChild(this.getBodySummaryComponent());

    return windowBodyHtml;
  }

  private getWindowFooter(): string {
    return `<gz-button id='confirm' type='full' color='primary'>
       ${translateFn('Create')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;
  }

  private getWindowTitle(): string {
    return translateFn('Create maintenance window');
  }
}
